import { call, put, select, takeEvery } from 'redux-saga/effects';
import Cookies from 'universal-cookie';
import { appInitAction, openModalAction, setIsFirstLoginAction } from '../../app/appActions';
import { RouterPaths } from "../../app/appConstants";
import appHistory from '../../app/appHistory';
import { appInitStartSaga, checkPasswordComplexitySaga } from '../../app/appSagas';
import { getAppFeatures } from "../../app/appSelectors";
import { failurePut } from '../../app/failurePut';
import { postRequest } from '../../app/request';
import { isClientMobile } from "../../components/ClientInfo/ClientInfo";
import { isEmailValid } from "../../utils/escapeUtils";
import { pushGtmEventAction } from "../GoogleTagManager/googleTagManagerActions";
import { MODALS_NAME } from "../Modals/modalsConstants";
import { setIsFirstShowIntro } from "../Tutorials/tutorialsActions";
import {
    autoLoginFailure,
    autoLoginSuccess,
    checkCodeFailure,
    checkCodeSuccess,
    createPasswordFailure,
    createPasswordSuccess,
    loginFailure,
    loginSuccess,
    logoutFailure,
    logoutSuccess,
    passwordResetFailure,
    passwordResetSuccess,
    passwordUpdateFailure,
    passwordUpdateSuccess,
    registrationAction,
    registrationFailure,
    registrationSuccess,
    resendCodeFailure,
    resendCodeSuccess,
    setChangeEmailSuccess,
    setRegistrationDataAction,
} from './loginActions';
import {
    AUTO_LOGIN_START,
    CHECK_CODE_START,
    CREATE_PASSWORD_START,
    LOGIN_START,
    LOGOUT_START,
    PASSWORD_RESET_START,
    PASSWORD_UPDATE_START,
    REGISTRATION_PREPARE,
    REGISTRATION_START,
    RESEND_CODE_START,
} from './loginConstants';

const browserCookies = new Cookies();

export function updateCookiesOnAuthSaga({ payload, cookies = browserCookies }) {
    cookies.set('token', payload.token, { path: '/', maxAge: 60 * 60 * 24 * 365 });
    cookies.set('secret', payload.secret, { path: '/', maxAge: 60 * 60 * 24 * 365 });
    cookies.remove('pin', { path: '/' });
    cookies.remove('pinOk', { path: '/' });
}

export function* loginStartSaga({ payload }) {
    try {
        const data = yield call(postRequest, '/user/login', payload);
        yield call(updateCookiesOnAuthSaga, { payload: { token: data.token, secret: data.secret } });
        yield put(loginSuccess(data.token));
        payload.onSuccessCallback && payload.onSuccessCallback();
        yield put(appInitAction());
    } catch (e) {
        yield call(failurePut, e, loginFailure);
        payload.onFailureCallback && payload.onFailureCallback(e.response?.data?.messageCode);
    }
}

export function* logoutStartSaga({ payload }) {
    try {
        yield call(postRequest, '/user/logout');
        browserCookies.remove('token', { path: '/' });
        browserCookies.remove('secret', { path: '/' });
    } catch (e) {
        yield call(failurePut, e, logoutFailure);
    } finally {
        yield put(pushGtmEventAction({ event: 'logout', source: payload?.source || null }));
        yield put(logoutSuccess());

        appHistory.push(RouterPaths.login);
    }
}

export function* validateRegisterDataSaga({ payload }) {
    if (!payload.email) {
        throw { messageCode: 'error.email_required' };
    }
    if (!isEmailValid(payload.email)) {
        throw { messageCode: 'error.email_not_valid' };
    }
    const features = yield select(getAppFeatures);
    if (features.register_without_verify) {
        const passwordComplexityError = yield call(checkPasswordComplexitySaga, payload.password);
        if (passwordComplexityError) {
            throw { messageCode: passwordComplexityError }
        }
    }
    if (!payload.acceptRules) {
        throw { messageCode: 'error.policy_not_accepted' };
    }
}

export function* registrationPrepareSaga({ payload }) {
    try {
        yield call(validateRegisterDataSaga, { payload });
        const features = yield select(getAppFeatures);
        if (features.register_without_verify) {
            yield put(setRegistrationDataAction(payload));
            yield call(postRequest, '/user/register-check', payload);
            yield put(openModalAction(MODALS_NAME.REGISTER_EMAIL_CHECK));
        } else {
            yield put(registrationAction(payload));
        }
    } catch (e) {
        yield call(failurePut, e, registrationFailure);
    }
}

export function* registrationStartSaga({ payload }) {
    try {
        if (payload.country) {
            browserCookies.set('country', payload.country, { path: '/', maxAge: 60 * 60 * 24 * 365 });
        }
        if (!isEmailValid(payload.email)) {
            throw { messageCode: 'error.email_not_valid' };
        }
        const locale = browserCookies.get('locale');
        const data = yield call(postRequest, '/user/register', {
            email: payload.email,
            phone: payload.phone,
            lang: locale,
            country: payload.country,
            password: payload.password,
        });
        yield put(registrationSuccess(payload));
        yield put(setChangeEmailSuccess(false));

        if (data?.token) {
            yield call(completeRegistrationSaga, data);
        } else {
            appHistory.push(`${RouterPaths.confirm}?register-email=${payload.email}`);
        }
    } catch (e) {
        yield call(failurePut, e, registrationFailure);
    }
}

export function* checkCodeSaga({ payload }) {
    try {
        if (!payload.code || !payload.email) {
            throw {
                messageCode: 'error.check_inputs'
            };
        }
        const url = payload.confirmEmail ? '/user/confirm-email' : '/user/check';
        yield call(postRequest, url, {
            email: payload.email,
            code: payload.code,
        });
        yield put(checkCodeSuccess());
        if (payload.onSuccess) {
            payload.onSuccess();
        }
    } catch (e) {
        yield call(failurePut, e, checkCodeFailure);
        if (payload.failureRedirectLink) {
            appHistory.push(payload.failureRedirectLink)
        }
    }
}

export function* createPasswordSaga({ payload }) {
    try {
        const passwordComplexityError = yield call(checkPasswordComplexitySaga, payload.password);
        if (passwordComplexityError) {
            yield put(registrationFailure(passwordComplexityError));
            return;
        }
        if (payload.password !== payload.password2) {
            yield put(registrationFailure('promo.fx.new_password.text2'));
            return;
        }
        const data = yield call(postRequest, '/user/password/create', {
            email: payload.email,
            code: payload.code,
            password: payload.password
        });
        yield call(completeRegistrationSaga, data);
        yield put(createPasswordSuccess());
    } catch (e) {
        yield call(failurePut, e, createPasswordFailure);
    }
}

export function* completeRegistrationSaga(data) {
    yield call(updateCookiesOnAuthSaga, { payload: { token: data.token, secret: data.secret } });
    yield put(setIsFirstLoginAction(true));
    if (!isClientMobile) {
        yield put(setIsFirstShowIntro(true));
    }
    yield call(appInitStartSaga);
}

export function* passwordResetSaga({ payload }) {
    try {
        yield call(postRequest, '/user/password/reset', {
            email: payload.email,
        });
        yield put(passwordResetSuccess());
    } catch (e) {
        yield call(failurePut, e, passwordResetFailure);
    }
}

export function* passwordUpdateSaga({ payload }) {
    try {
        if (payload.email.length < 4 || payload.code.length < 4) {
            yield put(registrationFailure('error.password_url'));
            return;
        }
        const passwordComplexityError = yield call(checkPasswordComplexitySaga, payload.password);
        if (passwordComplexityError) {
            yield put(registrationFailure(passwordComplexityError));
            return;
        }
        if (payload.password !== payload.password2) {
            yield put(registrationFailure('promo.fx.new_password.text2'));
            return;
        }
        const data = yield call(postRequest, '/user/password/update', {
            email: payload.email,
            code: payload.code,
            password: payload.password,
        });
        yield call(updateCookiesOnAuthSaga, { payload: { token: data.token, secret: data.secret } });
        yield put(passwordUpdateSuccess());
        yield put(appInitAction());
        payload.onSuccessCallback && payload.onSuccessCallback()
    } catch (e) {
        yield call(failurePut, e, passwordUpdateFailure);
    }
}

export function* resendCodeSaga({ payload }) {
    try {
        const { email, requestType } = payload;
        yield call(postRequest, '/user/resend', { email, requestType });
        yield put(resendCodeSuccess());
    } catch (e) {
        yield call(failurePut, e, resendCodeFailure);
    }
}

export function* autoLoginSaga({ payload }) {
    try {
        const data = yield call(postRequest, '/user/password/create', {
            email: payload.email,
            code: payload.code,
        });
        yield call(updateCookiesOnAuthSaga, { payload: { token: data.token, secret: data.secret } });
        yield put(setIsFirstLoginAction(true));
        yield call(appInitStartSaga);
        yield put(autoLoginSuccess());
        appHistory.push(`${RouterPaths.cashier}`);
    } catch (e) {
        yield call(failurePut, e, autoLoginFailure);
        appHistory.push(RouterPaths.brokenLink);
    }
}

export function* loginSagas() {
    yield takeEvery(LOGIN_START, loginStartSaga);
    yield takeEvery(LOGOUT_START, logoutStartSaga);
    yield takeEvery(PASSWORD_RESET_START, passwordResetSaga);
    yield takeEvery(PASSWORD_UPDATE_START, passwordUpdateSaga);
    yield takeEvery(REGISTRATION_START, registrationStartSaga);
    yield takeEvery(REGISTRATION_PREPARE, registrationPrepareSaga)
    yield takeEvery(CHECK_CODE_START, checkCodeSaga);
    yield takeEvery(CREATE_PASSWORD_START, createPasswordSaga);
    yield takeEvery(RESEND_CODE_START, resendCodeSaga);
    yield takeEvery(AUTO_LOGIN_START, autoLoginSaga);
}
